import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowUp, ArrowDown, Save, Upload, Trash2, Type, Image as ImageIcon, Pencil, RotateCcw, Plus } from "lucide-react";
import { toast } from "sonner";

const SW = 13.333, SH = 7.5;
const SECTIE_LABELS = { cover: "Cover", over_ons: "Over ons", producten: "Productdia's", bestellijst: "Bestellijst" };
const SLIDE_TYPES = [
  { k: "cover", label: "Cover" },
  { k: "over_ons", label: "Over ons" },
  { k: "product", label: "Productdia" },
  { k: "bestellijst", label: "Bestellijst" },
];
const ELEMENT_LABELS = {
  achtergrond: "Achtergrondbeeld", balk: "Kleurbalk", titel: "Titel", subtitel: "Subtitel",
  logo: "Logo", tekst: "Tekstblok", sku: "Artikelcode", hoofdfoto: "Hoofdfoto",
  miniaturen: "Miniaturen", prijs: "Prijs", verpakking: "Bestelbaar per", tabel: "Besteltabel", min_bestel: "Minimum bestelbedrag",
};
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
const r3 = (v) => Math.round(v * 1000) / 1000;
// Merkkleur die standaard geldt per element (getoond als de kleurkiezer geen eigen kleur heeft)
const STANDAARD_KLEUR = {
  balk: "primair", titel: "primair", subtitel: "secundair", tekst: "tekst",
  sku: "secundair", prijs: "accent", verpakking: "secundair", tabel: "primair", min_bestel: "accent",
};

// Voorbeeldinhoud per element voor de live preview
const SAMPLE = {
  cover: { titel: "Assortiment voor Uw klant", subtitel: "NextProduct" },
  over_ons: { titel: "Over ons", tekst: "Wij zijn een B2B-leverancier van cosmetica- en verzorgingsproducten met een focus op duurzaamheid en kwaliteit. Onze producten worden zorgvuldig geselecteerd voor de professionele markt." },
  product: {
    titel: "NextBrush – Bamboe Tandenborstel",
    sku: "NB1006",
    tekst: "Duurzame bamboe tandenborstel met zachte borstelharen. Het praktische venster in de verpakking laat het product goed uitkomen. Ideaal voor de bewuste consument.",
    prijs: "Uw prijs: € 1,01",
    verpakking: "Bestelbaar per: 6 stuks",
  },
  bestellijst: { titel: "Bestellijst" },
};

export function DiaVolgordeKaart({ cfg, isAdmin, onSaved }) {
  const [vol, setVol] = useState(cfg.slide_volgorde || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => setVol(cfg.slide_volgorde || []), [cfg.slide_volgorde]);

  const diaMap = Object.fromEntries((cfg.custom_dias || []).map((d) => [d.id, d]));
  const naamVan = (key) => {
    if (SECTIE_LABELS[key]) return SECTIE_LABELS[key];
    const d = diaMap[key.slice(7)];
    if (!d) return "Onbekende dia";
    return d.type === "tekst" ? (d.titel || "Tekst-dia") : (d.naam || "Afbeelding-dia");
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= vol.length) return;
    const next = [...vol];
    [next[i], next[j]] = [next[j], next[i]];
    setVol(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/presentatie", { slide_volgorde: vol });
      onSaved(data);
      toast.success("Dia-volgorde opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };
  const gewijzigd = JSON.stringify(vol) !== JSON.stringify(cfg.slide_volgorde || []);

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-volgorde-kaart">
      <h2 className="font-display text-xl font-medium">Dia-volgorde</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Bepaal de volgorde van de secties en eigen dia's in de presentatie.
      </p>
      <div className="space-y-1.5">
        {vol.map((key, i) => {
          const custom = key.startsWith("custom:");
          const d = custom ? diaMap[key.slice(7)] : null;
          return (
            <div key={key} className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
              data-testid={`presentatie-volgorde-rij-${i}`}>
              <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
              {custom ? (d?.type === "tekst" ? <Type className="h-4 w-4 text-muted-foreground shrink-0" /> : <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />) : null}
              <span className="text-sm flex-1 truncate">{naamVan(key)}</span>
              {custom && <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1.5 py-0.5">Eigen dia</span>}
              {isAdmin && (
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    className="h-7 w-7 rounded-md border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-30"
                    data-testid={`presentatie-volgorde-omhoog-${i}`}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === vol.length - 1}
                    className="h-7 w-7 rounded-md border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-30"
                    data-testid={`presentatie-volgorde-omlaag-${i}`}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isAdmin && (
        <div className="mt-4 flex justify-end">
          <Button disabled={saving || !gewijzigd} onClick={save} data-testid="presentatie-volgorde-save">
            <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Volgorde opslaan"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function GridEditorKaart({ cfg, isAdmin, onSaved }) {
  const [st, setSt] = useState("cover");
  const [modus, setModus] = useState("edit"); // edit | preview
  const [elems, setElems] = useState(cfg.slide_elementen || {});
  const [saving, setSaving] = useState(false);
  const [actief, setActief] = useState(null);
  const [demo, setDemo] = useState(null);
  const canvasRef = useRef(null);
  useEffect(() => setElems(cfg.slide_elementen || {}), [cfg.slide_elementen]);
  useEffect(() => {
    // Haal demo-product op om preview met echte foto's te tonen
    api.get("/instellingen/presentatie/demo-product")
      .then((r) => setDemo(r.data))
      .catch(() => setDemo(null));
  }, []);

  const huidige = elems[st] || {};
  const std = (cfg.standaard_elementen || {})[st] || {};
  const namen = Object.keys(std);
  const mk = cfg.merkkleuren || {};
  const kleurVan = (naam, fallback) => huidige[naam]?.kleur || mk[STANDAARD_KLEUR[naam]] || fallback;

  // Lighten/darken helper voor gradient-balk in preview
  const mix = (hex, factor, richting) => {
    const h = (hex || "#1A1A1A").replace("#", "");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    const f = (v) => richting > 0 ? Math.round(v + (255 - v) * factor) : Math.round(v * (1 - factor));
    return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
  };

  const logoUrl = cfg.logo?.id ? `${API_BASE}/instellingen/presentatie/bestanden/logo/${cfg.logo.id}` : null;
  const coverUrl = cfg.cover?.id ? `${API_BASE}/instellingen/presentatie/bestanden/cover/${cfg.cover.id}` : null;

  const zetElem = (naam, patch) =>
    setElems((cur) => ({ ...cur, [st]: { ...cur[st], [naam]: { ...cur[st][naam], ...patch } } }));

  const startDrag = (e, naam, mode) => {
    if (!isAdmin || modus !== "edit") return;
    e.preventDefault();
    e.stopPropagation();
    setActief(naam);
    const rect = canvasRef.current.getBoundingClientRect();
    const perPx = SW / rect.width;
    const start = { px: e.clientX, py: e.clientY, ...huidige[naam] };
    const onMove = (ev) => {
      const dx = (ev.clientX - start.px) * perPx;
      const dy = (ev.clientY - start.py) * perPx;
      setElems((cur) => {
        const el = { ...cur[st][naam] };
        if (mode === "move") {
          el.x = r3(clamp(start.x + dx, 0, SW - el.w));
          el.y = r3(clamp(start.y + dy, 0, SH - el.h));
        } else {
          el.w = r3(clamp(start.w + dx, 0.3, SW - el.x));
          el.h = r3(clamp(start.h + dy, 0.3, SH - el.y));
        }
        return { ...cur, [st]: { ...cur[st], [naam]: el } };
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/presentatie", { slide_elementen: elems });
      onSaved(data);
      toast.success("Dia-indeling opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };
  const reset = () => {
    setElems((cur) => ({ ...cur, [st]: JSON.parse(JSON.stringify(std)) }));
    toast.info("Standaardindeling hersteld — vergeet niet op te slaan");
  };
  const gewijzigd = JSON.stringify(elems) !== JSON.stringify(cfg.slide_elementen || {});

  // Voorbeeld-render per element (in preview-modus)
  const renderVoorbeeld = (naam, el) => {
    const style = {
      left: `${(el.x / SW) * 100}%`, top: `${(el.y / SH) * 100}%`,
      width: `${(el.w / SW) * 100}%`, height: `${(el.h / SH) * 100}%`,
    };
    const font = "Avenir, ui-sans-serif";
    if (naam === "balk" && st === "cover") {
      const c = kleurVan("balk", "#1A1A1A");
      return <div key={naam} className="absolute" style={{ ...style, background: `linear-gradient(to bottom, ${mix(c, 0.45, 1)}, ${mix(c, 0.25, -1)})` }} />;
    }
    if (naam === "logo") {
      return (
        <div key={naam} className="absolute flex items-center justify-end" style={style}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" className="max-h-full max-w-full object-contain object-right" />
            : <div className="text-[10px] uppercase tracking-wider text-muted-foreground border border-dashed border-muted-foreground/40 rounded px-1.5 py-0.5">logo</div>}
        </div>
      );
    }
    if (naam === "hoofdfoto") {
      const url = demo?.fotos?.[0]?.url;
      return (
        <div key={naam} className="absolute rounded overflow-hidden bg-gradient-to-br from-secondary to-secondary/40 flex items-center justify-center" style={style}>
          {url
            ? <img src={`${API_BASE}${url}`} alt="hoofd" className="w-full h-full object-contain" />
            : <ImageIcon className="h-6 w-6 text-muted-foreground/50" />}
        </div>
      );
    }
    if (naam === "miniaturen") {
      const gap = 0.02;
      const u2 = demo?.fotos?.[1]?.url;
      const u3 = demo?.fotos?.[2]?.url;
      return (
        <div key={naam} className="absolute flex" style={{ ...style, gap: `${gap * 100}%` }}>
          <div className="flex-1 bg-gradient-to-br from-secondary to-secondary/40 rounded overflow-hidden flex items-center justify-center">
            {u2 ? <img src={`${API_BASE}${u2}`} alt="thumb2" className="w-full h-full object-cover" /> : <ImageIcon className="h-4 w-4 text-muted-foreground/50" />}
          </div>
          <div className="flex-1 bg-gradient-to-br from-secondary to-secondary/40 rounded overflow-hidden flex items-center justify-center">
            {u3 ? <img src={`${API_BASE}${u3}`} alt="thumb3" className="w-full h-full object-cover" /> : <ImageIcon className="h-4 w-4 text-muted-foreground/50" />}
          </div>
        </div>
      );
    }
    if (naam === "tabel") {
      return (
        <div key={naam} className="absolute overflow-hidden" style={style}>
          <div className="grid grid-cols-4 gap-x-2 border-b" style={{ color: kleurVan("tabel", "#1A1A1A"), borderColor: kleurVan("tabel", "#1A1A1A") + "40", fontFamily: font }}>
            <div className="text-[11px] font-bold py-0.5">Artikelcode</div>
            <div className="text-[11px] font-bold py-0.5">Product</div>
            <div className="text-[11px] font-bold py-0.5">Uw prijs</div>
            <div className="text-[11px] font-bold py-0.5">Aantal</div>
          </div>
          {["NB1006", "NB1024", "NB1055"].map((sku, i) => (
            <div key={i} className="grid grid-cols-4 gap-x-2 text-[10px] py-0.5 text-foreground/80" style={{ fontFamily: font }}>
              <div>{sku}</div><div className="truncate">Voorbeeldproduct {i + 1}</div><div>€ {(1.01 + i * 0.5).toFixed(2).replace(".", ",")}</div><div>6 stuks</div>
            </div>
          ))}
        </div>
      );
    }
    // Text-elementen
    const demoPrijs = demo?.verkoopprijs ? `Uw prijs: € ${Number(demo.verkoopprijs).toFixed(2).replace(".", ",")}` : SAMPLE.product.prijs;
    const demoBP = demo?.bestelbaar_per ? `Bestelbaar per: ${demo.bestelbaar_per} stuks` : SAMPLE.product.verpakking;
    const productSample = { ...SAMPLE.product, titel: demo?.naam || SAMPLE.product.titel, sku: demo?.sku || SAMPLE.product.sku, prijs: demoPrijs, verpakking: demoBP };
    let tekst = (st === "product" ? productSample : SAMPLE[st])?.[naam] || ELEMENT_LABELS[naam] || naam;
    const isTitel = ["titel"].includes(naam);
    const isSub = ["subtitel", "sku"].includes(naam);
    const isPrijs = naam === "prijs";
    const bold = isTitel || isPrijs;
    const c = kleurVan(naam, "#1A1A1A");
    const balkAan = st === "cover" && huidige["balk"]?.aan && ["titel", "subtitel"].includes(naam);
    const finalKleur = balkAan && !huidige[naam]?.kleur ? "#FFFFFF" : c;
    // sizing pt op basis van box-hoogte (schaal naar preview)
    const fontSize = isTitel ? "clamp(11px, 2.1vw, 22px)"
      : isPrijs ? "clamp(10px, 1.6vw, 17px)"
      : isSub ? "clamp(9px, 1.1vw, 12px)"
      : "clamp(9px, 1.1vw, 13px)";
    return (
      <div key={naam} className="absolute overflow-hidden" style={{ ...style, color: finalKleur, fontFamily: font, fontWeight: bold ? 600 : 400, fontSize, lineHeight: 1.25 }}>
        <div className="whitespace-pre-wrap">{tekst}</div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-grid-kaart">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-medium">Dia-indeling</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Zet per dia-type elementen aan of uit, versleep ze op het raster en geef ze eventueel een eigen kleur. Schakel naar <b>Voorbeeld</b> voor een live weergave met kleuren en teksten.
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg border border-border bg-background" data-testid="presentatie-grid-modus">
          <button onClick={() => setModus("edit")} data-testid="presentatie-grid-modus-edit"
            className={`px-3 h-7 rounded-md text-xs font-medium ${modus === "edit" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            Bewerken
          </button>
          <button onClick={() => setModus("preview")} data-testid="presentatie-grid-modus-preview"
            className={`px-3 h-7 rounded-md text-xs font-medium ${modus === "preview" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            Voorbeeld
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4 mb-4">
        {SLIDE_TYPES.map(({ k, label }) => (
          <button key={k} onClick={() => { setSt(k); setActief(null); }}
            className={`h-8 px-3 rounded-md text-sm border transition-colors ${st === k ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background hover:bg-secondary"}`}
            data-testid={`presentatie-grid-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <div ref={canvasRef}
        className={`relative w-full aspect-video rounded-lg border border-border overflow-hidden select-none ${modus === "preview" ? "bg-white" : "bg-secondary/60"}`}
        style={modus === "edit"
          ? { backgroundImage: "linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)", backgroundSize: "7.5% 13.333%" }
          : undefined}
        data-testid="presentatie-grid-canvas">
        {/* Cover-achtergrond in preview */}
        {modus === "preview" && st === "cover" && coverUrl && huidige["achtergrond"]?.aan && (
          <img src={coverUrl} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {namen.filter((n) => "x" in std[n] && huidige[n]?.aan).map((naam) => {
          const el = huidige[naam];
          if (modus === "preview") {
            return renderVoorbeeld(naam, el);
          }
          return (
            <div key={naam}
              onPointerDown={(e) => startDrag(e, naam, "move")}
              className={`absolute rounded border flex items-start justify-start px-1.5 py-0.5 text-[10px] font-medium cursor-move transition-colors ${actief === naam ? "border-primary bg-primary/15 text-primary z-10" : "border-primary/40 bg-primary/5 text-foreground/70 hover:bg-primary/10"}`}
              style={{
                left: `${(el.x / SW) * 100}%`, top: `${(el.y / SH) * 100}%`,
                width: `${(el.w / SW) * 100}%`, height: `${(el.h / SH) * 100}%`,
              }}
              data-testid={`presentatie-grid-element-${naam}`}>
              <span className="truncate pointer-events-none">{ELEMENT_LABELS[naam] || naam}</span>
              {isAdmin && (
                <div onPointerDown={(e) => startDrag(e, naam, "resize")}
                  className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-sm bg-primary cursor-nwse-resize"
                  data-testid={`presentatie-grid-resize-${naam}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 space-y-1.5">
        {namen.map((naam) => {
          const kleurbaar = "kleur" in std[naam];
          const eigenKleur = huidige[naam]?.kleur || null;
          const standaardKleur = (cfg.merkkleuren || {})[STANDAARD_KLEUR[naam]] || "#1A1A1A";
          return (
            <div key={naam} className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer w-56">
                <Checkbox checked={!!huidige[naam]?.aan} disabled={!isAdmin}
                  onCheckedChange={(v) => zetElem(naam, { aan: !!v })}
                  data-testid={`presentatie-grid-toggle-${naam}`} />
                <span className="truncate">{ELEMENT_LABELS[naam] || naam}</span>
                {!("x" in std[naam]) && <span className="text-[10px] text-muted-foreground shrink-0">(vaste positie)</span>}
              </label>
              {kleurbaar && (
                <div className="flex items-center gap-1.5">
                  <input type="color" value={eigenKleur || standaardKleur} disabled={!isAdmin}
                    onChange={(e) => zetElem(naam, { kleur: e.target.value })}
                    className="h-7 w-10 rounded border border-border cursor-pointer bg-background"
                    title={eigenKleur ? "Eigen kleur" : "Standaard (merkkleur)"}
                    data-testid={`presentatie-grid-kleur-${naam}`} />
                  {eigenKleur ? (
                    <button onClick={() => zetElem(naam, { kleur: null })} disabled={!isAdmin}
                      className="text-[11px] text-muted-foreground hover:text-destructive underline underline-offset-2"
                      data-testid={`presentatie-grid-kleur-reset-${naam}`}>
                      herstel
                    </button>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">merkkleur</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div className="mt-5 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={reset} data-testid="presentatie-grid-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Standaardindeling
          </Button>
          <Button disabled={saving || !gewijzigd} onClick={save} data-testid="presentatie-grid-save">
            <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Indeling opslaan"}
          </Button>
        </div>
      )}
    </div>
  );
}

function TekstDiaDialog({ open, onOpenChange, dia, onSaved }) {
  const [titel, setTitel] = useState("");
  const [tekst, setTekst] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setTitel(dia?.titel || "");
      setTekst(dia?.tekst || "");
    }
  }, [open, dia]);
  const save = async () => {
    if (!titel.trim() && !tekst.trim()) {
      toast.error("Geef een titel of tekst op");
      return;
    }
    setBusy(true);
    try {
      const { data } = dia
        ? await api.put(`/instellingen/presentatie/dias/tekst/${dia.id}`, { titel, tekst })
        : await api.post("/instellingen/presentatie/dias/tekst", { titel, tekst });
      onSaved(data);
      onOpenChange(false);
      toast.success(dia ? "Tekst-dia bijgewerkt" : "Tekst-dia toegevoegd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="presentatie-tekstdia-dialog">
        <DialogHeader>
          <DialogTitle>{dia ? "Tekst-dia bewerken" : "Tekst-dia toevoegen"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Titel</Label>
            <Input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Bijv. Onze certificeringen"
              data-testid="presentatie-tekstdia-titel" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Tekst</Label>
            <Textarea rows={6} value={tekst} onChange={(e) => setTekst(e.target.value)}
              placeholder="Inhoud van de dia… (wordt bij een andere taal automatisch vertaald met AI)"
              data-testid="presentatie-tekstdia-tekst" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={save} disabled={busy} data-testid="presentatie-tekstdia-save">
            {busy ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomDiasKaart({ cfg, isAdmin, onSaved }) {
  const [diaBusy, setDiaBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bewerkDia, setBewerkDia] = useState(null);

  const uploadDia = async (file) => {
    if (!file) return;
    setDiaBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/instellingen/presentatie/bestanden/dia", fd);
      onSaved(data);
      toast.success("Afbeelding-dia toegevoegd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload mislukt");
    } finally {
      setDiaBusy(false);
    }
  };
  const verwijderDia = async (id) => {
    try {
      const { data } = await api.delete(`/instellingen/presentatie/bestanden/dia/${id}`);
      onSaved(data);
      toast.success("Dia verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const dias = cfg.custom_dias || [];
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-dias-kaart">
      <h2 className="font-display text-xl font-medium">Eigen dia's</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Voeg afbeelding-dia's (bijv. certificaten) of tekst-dia's toe. De positie in de presentatie bepaal je hierboven bij "Dia-volgorde".
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {dias.map((d) => (
          <div key={d.id} className="relative group border border-border rounded-lg overflow-hidden bg-secondary"
            data-testid={`presentatie-dia-${d.id}`}>
            {d.type === "tekst" ? (
              <div className="h-28 w-full p-3 flex flex-col bg-background">
                <div className="flex items-center gap-1.5 text-xs font-medium truncate">
                  <Type className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{d.titel || "Tekst-dia"}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 line-clamp-4 whitespace-pre-line">{d.tekst}</p>
              </div>
            ) : (
              <img src={`${API_BASE}/instellingen/presentatie/bestanden/dia/${d.id}`}
                alt={d.naam} className="h-28 w-full object-contain" />
            )}
            <div className="px-2 py-1 text-xs text-muted-foreground truncate border-t border-border">
              {d.type === "tekst" ? "Tekst-dia" : d.naam}
            </div>
            {isAdmin && (
              <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {d.type === "tekst" && (
                  <button onClick={() => { setBewerkDia(d); setDialogOpen(true); }}
                    className="h-7 w-7 rounded-md bg-background/90 border border-border flex items-center justify-center"
                    data-testid={`presentatie-dia-bewerk-${d.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={() => verwijderDia(d.id)}
                  className="h-7 w-7 rounded-md bg-background/90 border border-border flex items-center justify-center"
                  data-testid={`presentatie-dia-verwijder-${d.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </button>
              </div>
            )}
          </div>
        ))}
        {isAdmin && (
          <>
            <label className="cursor-pointer h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <input type="file" accept="image/*" className="hidden" disabled={diaBusy}
                onChange={(e) => { uploadDia(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="presentatie-dia-upload" />
              <Upload className="h-5 w-5 mb-1" />
              <span className="text-xs">{diaBusy ? "Uploaden…" : "Afbeelding-dia"}</span>
            </label>
            <button onClick={() => { setBewerkDia(null); setDialogOpen(true); }}
              className="h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              data-testid="presentatie-tekstdia-toevoegen">
              <Plus className="h-5 w-5 mb-1" />
              <span className="text-xs">Tekst-dia</span>
            </button>
          </>
        )}
        {!isAdmin && dias.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground">Nog geen eigen dia's.</div>
        )}
      </div>
      <TekstDiaDialog open={dialogOpen} onOpenChange={setDialogOpen} dia={bewerkDia} onSaved={onSaved} />
    </div>
  );
}
